/** @format */

import {Skeleton} from '@mui/material';
import {Trans} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {getAdType} from 'pages/HomeGrouping/helpers/getAdType';

import {trackOverviewEvent} from '../../helpers/trackOverviewEvent';
import {type ProjectCardModel, type ViewMode} from '../../types';
import {CardHeader} from './CardHeader';
import {StatusRow} from './StatusRow';
import {MetricsBlock} from './MetricsBlock';
import {useCardBody} from './useCardBody';
import {
  BodyErrorWrapper,
  BodyErrorText,
  RetryButton,
  ProjectCardClasses as classes,
  ProjectCardRoot as Root,
} from './styles';

interface Props {
  model: ProjectCardModel;
  viewMode: ViewMode;
  now: Date;
}

const ProjectCard = ({model, viewMode, now}: Props) => {
  const navigate = useNavigate();
  const {mixpanel} = useMixpanelContext();
  const {switchCompany, switchAdType} = useCompanyContext();

  const {ref, inView, company, loading, error, refetch} = useCardBody({
    companyId: model.companyId,
    period: model.period,
    now,
  });

  // Error state: only when the card IS in view AND the query finished without data (or returned an Apollo error).
  // When not yet in view the query is skipped → loading:false, data:undefined, company:null — that's NOT an error.
  const queryFinishedWithoutData = inView && !loading && !error && company === null;
  const showError = Boolean(error) || queryFinishedWithoutData;

  const handleCardClick = () => {
    trackOverviewEvent(mixpanel, 'click_overview_project_card', {project_uuid: model.uuid});
    const adType = getAdType(model.adSystems[0] ?? null);
    switchAdType(adType);
    switchCompany({id: model.companyId, uuid: model.uuid, name: model.name});
    navigate('/home/main/status');
  };

  const handleSetupMediaplan = () => {
    trackOverviewEvent(mixpanel, 'click_overview_setup_mediaplan_button', {
      project_uuid: model.uuid,
    });
    navigate(`/home/project/periods?companyId=${model.companyId}`);
  };

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    refetch();
  };

  const renderBody = () => {
    if (showError) {
      return (
        <BodyErrorWrapper className={classes.bodyError}>
          <BodyErrorText className={classes.bodyErrorText}>
            <Trans>Не удалось загрузить данные</Trans>
          </BodyErrorText>
          <RetryButton data-testid='card-body-retry' onClick={handleRetry}>
            <Trans>Повторить</Trans>
          </RetryButton>
        </BodyErrorWrapper>
      );
    }

    if (!company) {
      // Still loading or not yet in view — show skeleton placeholder.
      return <Skeleton variant='rectangular' height={200} data-testid='card-body-skeleton' />;
    }

    return (
      <MetricsBlock
        viewMode={viewMode}
        company={company}
        now={now}
        onSetupMediaplan={handleSetupMediaplan}
      />
    );
  };

  return (
    <Root
      ref={ref}
      className={classes.root}
      data-testid={`card-${model.companyId}`}
      onClick={handleCardClick}
    >
      <CardHeader
        adSystems={model.adSystems}
        analytics={model.analytics}
        name={model.name}
        mediaplanType={model.mediaplanType ?? null}
      />
      <StatusRow
        statuses={model.statuses}
        triggered={model.triggered}
        critical={model.critical}
        hasMediaplan={model.hasMediaplan}
      />
      {renderBody()}
    </Root>
  );
};

export {ProjectCard};
